import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useTheme } from "@mui/material/styles";

import useData from "../../hooks/useData";
import useBrowser from "../../hooks/useBrowser";

import Image from "../elements/Image";

// CAD formatter preserved
const formatCAD = (value) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(
    value
  );

export default function OrderForm({
  title,
  skuList,
  order,
  onSave,
  onCancel,
  readOnly = false,
}) {
  const { findOne } = useData();
  const theme = useTheme();
  const { windowHeight } = useBrowser();

  // Items in order
  const [items, setItems] = useState([]);
  const [openLines, setOpenLines] = useState([]); // track by skuId
  const [modalImage, setModalImage] = useState(null);

  // SKU modal state
  const [skuPickerVisible, setSkuPickerVisible] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [expandedOrderCategories, setExpandedOrderCategories] = useState([]);

  // Signature state
  const [signature, setSignature] = useState(null);
  const [showSignature, setShowSignature] = useState(false);
  const [stickyHeight, setStickyHeight] = useState(0);
  const [signatureHeight, setSignatureHeight] = useState(0);

  // Optimized SKU lookup
  const skuMap = useMemo(
    () => Object.fromEntries(skuList.map((s) => [s.id, s])),
    [skuList]
  );

  useEffect(() => {
    if (!order) return;
    setItems(
      typeof order.items === "string"
        ? JSON.parse(order.items)
        : order.items || []
    );
    if (order.signature) {
      setSignature(
        typeof order.signature === "string"
          ? JSON.parse(order.signature)
          : order.signature
      );
    }
  }, [order]);

  const toggleLine = (skuId) => {
    setOpenLines((prev) =>
      prev.includes(skuId)
        ? prev.filter((id) => id !== skuId)
        : [...prev, skuId]
    );
  };

  const addItem = () => {
    if (!skuList || skuList.length === 0) {
      alert("No SKUs available. Please load SKU list first.");
      return;
    }
    setSkuPickerVisible(true);
  };

  const handleSkuTap = (skuId) => {
    const skuMeta = skuMap[skuId];
    if (!skuMeta) return;

    const exists = findOne(items, { skuId: skuMeta.skuId });
    if (exists) {
      alert("SKU already added");
      return;
    }

    setItems((prev) => [
      ...prev,
      { skuId: skuMeta.skuId, quantity: 1, price: skuMeta.price ?? 0 },
    ]);

    const category = skuMeta.category || "Uncategorized";
    setExpandedOrderCategories((prev) =>
      prev.includes(category) ? prev : [...prev, category]
    );

    setSkuPickerVisible(false);
  };

  const getItemIndexBySkuId = (skuId) =>
    items.findIndex((it) => it.skuId === skuId);

  const updateItemBySkuId = (skuId, field, value) => {
    const idx = getItemIndexBySkuId(skuId);
    if (idx < 0) return;
    const updated = [...items];
    updated[idx] = { ...updated[idx], [field]: value };

    if (field === "skuId" && value) {
      const skuMeta = skuMap[value];
      if (skuMeta) updated[idx].price = skuMeta.price ?? 0;
    }

    setItems(updated);
  };

  const adjustQuantityBySkuId = (skuId, delta) => {
    const idx = getItemIndexBySkuId(skuId);
    if (idx < 0) return;
    const updated = [...items];
    const qty = parseInt(updated[idx].quantity, 10) || 0;
    const next = qty + delta;
    updated[idx].quantity = next > 0 ? next : 0;
    setItems(updated);
  };

  const removeItem = (skuId) => {
    setItems(items.filter((item) => item.skuId !== skuId));
    setOpenLines(openLines.filter((id) => id !== skuId));
  };

  const handleCancel = () => {
    onCancel();
  };

  const handleSave = () => {
    const normalized = items.map((item) => ({
      ...item,
      quantity: parseInt(item.quantity, 10),
      price: parseFloat(item.price),
    }));
    onSave({ ...order, items: normalized, signature });
  };

  const extendedItems = items.map((item) => {
    const qty = parseInt(item.quantity, 10) || 0;
    const price = parseFloat(item.price) || 0;
    return { ...item, extended: qty * price, skuMeta: skuMap[item.skuId] };
  });

  // Aggregates
  const categoryAggregates = extendedItems.reduce((acc, item) => {
    const category = item.skuMeta?.category || "Uncategorized";
    if (!acc[category]) {
      acc[category] = { subtotal: 0, quantity: 0 };
    }
    acc[category].subtotal += item.extended;
    acc[category].quantity += parseInt(item.quantity, 10) || 0;
    return acc;
  }, {});

  const orderTotal = extendedItems.reduce(
    (sum, item) => sum + item.extended,
    0
  );
  const totalSkus = extendedItems.length;
  const totalQuantity = extendedItems.reduce(
    (sum, item) => sum + (parseInt(item.quantity, 10) || 0),
    0
  );

  const groupedItems = extendedItems.reduce((acc, item) => {
    const category = item.skuMeta?.category || "Uncategorized";
    if (!acc[category]) acc[category] = [];
    acc[category].push(item);
    return acc;
  }, {});

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: `${windowHeight - 60}px`,
        flex: 1,
      }}
    >
      {/* Main content: categories + items */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          pb: 6,
          p: 2,
        }}
      >
        {Object.entries(groupedItems).map(([category, items]) => (
          <Box
            key={category}
            sx={{ mb: 1.5 }}
          >
            {/* Category header */}
            <Box
              sx={{
                py: 1,
                px: 1,
                bgcolor: "#ddd",
                borderRadius: 1,
                mb: 0.5,
                cursor: "pointer",
              }}
              onClick={() =>
                setExpandedOrderCategories((prev) =>
                  prev.includes(category)
                    ? prev.filter((c) => c !== category)
                    : [...prev, category]
                )
              }
            >
              <Typography
                sx={{ fontSize: 12, fontWeight: "bold", color: "#222" }}
              >
                {category}
              </Typography>
              <Typography
                sx={{ fontSize: 10, fontWeight: "bold", color: "#222" }}
              >
                SKUs: {items.length} | Qty:{" "}
                {categoryAggregates[category]?.quantity || 0} | Subtotal:{" "}
                {formatCAD(categoryAggregates[category]?.subtotal || 0)}
              </Typography>
            </Box>

            {/* Items inside category */}
            {expandedOrderCategories.includes(category) &&
              items.map((item) => {
                const isOpen = openLines.includes(item.skuId);
                return (
                  <Box
                    key={item.skuId}
                    sx={{ mb: 1 }}
                  >
                    {!isOpen ? (
                      <Box
                        sx={{
                          p: 1,
                          bgcolor: "#f2f2f2",
                          borderRadius: 1,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        {item.skuMeta?.image && (
                          <IconButton
                            onClick={() => setModalImage(item.skuMeta.image)}
                          >
                            <img
                              src={
                                item.skuMeta.image.startsWith("data:")
                                  ? item.skuMeta.image
                                  : `data:image/png;base64,${item.skuMeta.image}`
                              }
                              alt="SKU"
                              style={{
                                width: 32,
                                height: 32,
                                borderRadius: 4,
                                objectFit: "cover",
                              }}
                            />
                          </IconButton>
                        )}
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography
                            sx={{
                              fontSize: 12,
                              fontWeight: "bold",
                              overflowWrap: "anywhere",
                            }}
                          >
                            {item.skuMeta?.name || item.skuId}
                          </Typography>
                          <Typography
                            sx={{ fontSize: 10 }}
                            color="text.secondary"
                          >
                            Qty: {item.quantity}, Ext:{" "}
                            {formatCAD(item.extended)}
                          </Typography>
                        </Box>
                        {!readOnly && (
                          <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
                            {/* <Button
                              size="small"
                              variant="outlined"
                              onClick={() =>
                                adjustQuantityBySkuId(item.skuId, 1)
                              }
                            >
                              +
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() =>
                                adjustQuantityBySkuId(item.skuId, -1)
                              }
                            >
                              -
                            </Button> */}
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => toggleLine(item.skuId)}
                            >
                              Edit
                            </Button>
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Box sx={{ p: 1, bgcolor: "#e9f7ff", borderRadius: 1 }}>
                        {item.skuMeta?.image && (
                          <IconButton
                            onClick={() => setModalImage(item.skuMeta.image)}
                          >
                            <img
                              src={
                                item.skuMeta.image.startsWith("data:")
                                  ? item.skuMeta.image
                                  : `data:image/png;base64,${item.skuMeta.image}`
                              }
                              alt="SKU"
                              style={{
                                width: 64,
                                height: 64,
                                borderRadius: 6,
                                marginBottom: 8,
                                objectFit: "cover",
                              }}
                            />
                          </IconButton>
                        )}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: "bold",
                              overflowWrap: "anywhere",
                            }}
                          >
                            {item.skuMeta?.name}
                          </Typography>
                          <TextField
                            type="number"
                            value={item.quantity}
                            onChange={(e) =>
                              updateItemBySkuId(
                                item.skuId,
                                "quantity",
                                e.target.value
                              )
                            }
                            placeholder="Qty"
                            size="small"
                            sx={{ width: 75 }}
                          />
                        </Box>

                        <Typography
                          sx={{ fontWeight: "bold", textAlign: "right", my: 1 }}
                        >
                          {formatCAD(item.extended)}
                        </Typography>

                        {!readOnly && (
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mt: 1,
                            }}
                          >
                            <Button
                              size="small"
                              color="error"
                              variant="outlined"
                              onClick={() => removeItem(item.skuId)}
                              startIcon={<DeleteIcon />}
                            >
                              Remove
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => toggleLine(item.skuId)}
                            >
                              Close
                            </Button>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })}
          </Box>
        ))}
      </Box>

      {/* Signature block */}
      <Box
        sx={{
          mt: 3,
          p: 1,
          borderTop: "1px solid #ccc",
          // height: showSignature ? stickyHeight + 300 : stickyHeight + 20,
        }}
      >
        <Button onClick={() => setShowSignature(!showSignature)}>
          {`${showSignature ? "Hide" : "Show"} Signature`}
        </Button>
        {showSignature && (
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: "bold", mb: 1 }}>
              Store Approval Signature:
            </Typography>
            <Image src={signature ? signature.signature : ""} />
            <Typography>
              {`${signature.name} ${signature.position ? ":" : ""} ${
                signature.position
              }`}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Sticky totals + actions */}
      <Paper
        elevation={8}
        sx={{
          position: "sticky",
          bottom: 0,
          left: 0,
          right: 0,
          p: 1.5,
          bgcolor: "#ffffff",
          borderTop: "1px solid #ddd",
          boxShadow: "0px -4px 12px rgba(0,0,0,0.1)",
          display: "grid",
          gap: 1,
        }}
        ref={(el) => {
          if (el) setStickyHeight(el.offsetHeight);
        }}
      >
        <Typography sx={{ fontWeight: "bold", fontSize: 12 }}>
          Order Total: {formatCAD(orderTotal)} | SKUs: {totalSkus} | Qty:{" "}
          {totalQuantity}
        </Typography>

        {!readOnly ? (
          <Box
            sx={{ display: "flex", justifyContent: "space-between", gap: 1.5 }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={addItem}
              size="small"
            >
              Add Item
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              size="small"
            >
              Save Order
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleCancel}
              size="small"
            >
              Cancel
            </Button>
          </Box>
        ) : (
          <Box
            sx={{ display: "flex", justifyContent: "space-between", gap: 1.5 }}
          >
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleCancel}
            >
              Exit
            </Button>
          </Box>
        )}
      </Paper>

      {/* Image Modal */}
      <Dialog
        open={!!modalImage}
        onClose={() => setModalImage(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ textAlign: "center" }}>
          {modalImage && (
            <img
              src={
                modalImage.startsWith("data:")
                  ? modalImage
                  : `data:image/png;base64,${modalImage}`
              }
              alt="SKU"
              style={{ maxWidth: "100%", height: "auto", borderRadius: 8 }}
            />
          )}
          <Box
            sx={{
              mt: 2,
              display: "flex",
              justifyContent: "space-between",
              gap: 1.5,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setModalImage(null)}
            >
              Close
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* SKU Picker Modal */}
      <Dialog
        open={skuPickerVisible}
        onClose={() => setSkuPickerVisible(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Select SKU</DialogTitle>
        <DialogContent
          dividers
          sx={{ maxHeight: 400 }}
        >
          {Object.entries(
            skuList.reduce((acc, sku) => {
              const cat = sku.category || "Uncategorized";
              if (!acc[cat]) acc[cat] = [];
              acc[cat].push(sku);
              return acc;
            }, {})
          ).map(([category, skus]) => (
            <Accordion
              key={category}
              expanded={expandedCategories.includes(category)}
              onChange={() =>
                setExpandedCategories((prev) =>
                  prev.includes(category)
                    ? prev.filter((c) => c !== category)
                    : [...prev, category]
                )
              }
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "bold" }}
                >
                  {category}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {skus.map((sku) => (
                  <Box
                    key={sku.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      py: 1,
                      pl: 2,
                      cursor: "pointer",
                    }}
                    onClick={() => handleSkuTap(sku.id)}
                  >
                    {sku.image && (
                      <img
                        src={
                          sku.image.startsWith("data:")
                            ? sku.image
                            : `data:image/png;base64,${sku.image}`
                        }
                        alt={sku.name}
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 4,
                          marginRight: 8,
                          objectFit: "cover",
                        }}
                      />
                    )}
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography sx={{ fontSize: 15 }}>{sku.name}</Typography>
                      <Typography
                        sx={{ fontSize: 12 }}
                        color="text.secondary"
                      >
                        {formatCAD(sku.price ?? 0)}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </DialogContent>
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            gap: 1.5,
          }}
        >
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setSkuPickerVisible(false)}
          >
            Cancel
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
}
